/* eslint-disable react-refresh/only-export-components -- context module exposes hook plus provider */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { logger as globalLogger } from '@/lib/logger';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import type { OrganizationSettings as SharedOrganizationSettings, PrintMode } from '@threadheads/shared';
import { organizationSettingsSchema } from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import { resolveBranding, writeBrandingCache, clearBrandingCache } from '@/lib/branding';
import i18next from 'i18next';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  shopify_domain: string;
  /** Pure-fulfilment 3PL provider — not expected to attach its own Shopify store. */
  is_3pl_provider: boolean;
  default_currency: string | null;
  plan_type: string | null;
  subscription_status: string | null;
  is_active: boolean;
  created_at: string;
  settings?: SharedOrganizationSettings | null;
  thumbnail_url?: string | null;
  /**
   * BLU-1376: the org was created through Platform, Create store and starts in
   * setup mode. Optional and read as false when missing (fail open: an older
   * backend, or a live org, is never in setup mode).
   */
  setup_required?: boolean;
  /** BLU-1376: the setup latch; `null` = setup not finished. */
  setup_completed_at?: string | null;
  /** BLU-1376: the saved answer to "How do you print?". The wizard reads it from the onboarding status. */
  print_mode?: PrintMode | null;
}

interface OrganizationContextType {
  organization: Organization | null;
  isLoading: boolean;
  /**
   * True once the first org load of this session has settled (success, an
   * inactive org or an error alike). ProtectedRoute shows its loading screen
   * only before that, so an org switch never unmounts the app. Never set back
   * to false (not on a switch, a reload or a sign-out).
   */
  initialised: boolean;
  reloadOrganization: () => void;
}

const OrganizationContext = createContext<OrganizationContextType>({
  organization: null,
  isLoading: true,
  initialised: false,
  reloadOrganization: () => {},
});

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [initialised, setInitialised] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const { user, profile, loading: authLoading } = useAuth();
  const trpcUtils = trpc.useUtils();

  // Function to trigger organization reload
  const reloadOrganization = () => {
    setReloadTrigger(prev => prev + 1);
  };

  // Load organization from active selection (localStorage) or user's profile
  useEffect(() => {
    const loadOrganization = async () => {
      // Wait for auth to complete
      if (authLoading) {
        return;
      }

      // If no user is authenticated, don't try to load organization
      if (!user || !profile) {
        setIsLoading(false);
        return;
      }

      // Resolve active org: prefer selected, fallback to profile.organization_id
      const selectedOrgId =
        localStorage.getItem('activeOrgId') || localStorage.getItem('selected_org_id') || profile.organization_id;

      // Check if we have an org id at all
      if (!selectedOrgId) {
        globalLogger.error('User profile missing organization_id', {
          operation: 'load_organization',
          userId: user.id,
        });

        toast({
          title: i18next.t('common:org.configError'),
          description: i18next.t('common:org.notLinked'),
          variant: 'destructive',
        });

        setIsLoading(false);
        setInitialised(true);
        return;
      }

      try {
        const storedOrgId = localStorage.getItem('activeOrgId') || localStorage.getItem('selected_org_id') || '';
        if (!storedOrgId && selectedOrgId) {
          try { localStorage.setItem('activeOrgId', selectedOrgId); } catch { /* ignore */ }
        }

        globalLogger.info('Loading organization', {
          operation: 'load_organization',
          organizationId: selectedOrgId,
          userId: user.id,
        });

        const response = await trpcUtils.organization.getCurrent.fetch();
        const data = response?.organization;

        if (!data) {
          throw new Error('Organization not found');
        }

        // Verify organization is active
        if (!data.is_active) {
          globalLogger.error('Organization is inactive', {
            operation: 'load_organization',
            organizationId: data.id,
            organizationName: data.name,
          });

          toast({
            title: i18next.t('common:org.accountSuspended'),
            description: i18next.t('common:org.suspendedDescription'),
            variant: 'destructive',
          });

          setOrganization(null);
          setIsLoading(false);
          return;
        }

        // Verify subscription is active
        if (data.subscription_status !== 'active') {
          globalLogger.warn('Organization subscription not active', {
            operation: 'load_organization',
            organizationId: data.id,
            subscriptionStatus: data.subscription_status,
          });

          toast({
            title: i18next.t('common:org.subscriptionIssue'),
            description: i18next.t('common:org.subscriptionStatus', { status: data.subscription_status }),
            variant: 'destructive',
          });
        }

        globalLogger.info('Organization loaded successfully', {
          operation: 'load_organization',
          organizationId: data.id,
          organizationName: data.name,
          subscriptionStatus: data.subscription_status,
        });

        const rawSettings = (data as { settings?: unknown }).settings;
        let parsedSettings: SharedOrganizationSettings | null = null;

        if (rawSettings && typeof rawSettings === 'object' && !Array.isArray(rawSettings)) {
          const settingsResult = organizationSettingsSchema.safeParse(rawSettings);
          if (settingsResult.success) {
            parsedSettings = settingsResult.data;
          } else {
            globalLogger.warn('Invalid organization settings payload', {
              operation: 'load_organization',
              organizationId: data.id,
              issues: settingsResult.error.flatten().fieldErrors,
            });
          }
        }

        setOrganization({
          id: data.id,
          name: data.name ?? '',
          slug: data.slug ?? '',
          shopify_domain: data.shopify_domain ?? '',
          is_3pl_provider: data.is_3pl_provider ?? false,
          default_currency: data.default_currency ?? null,
          plan_type: data.plan_type,
          subscription_status: data.subscription_status,
          is_active: data.is_active,
          created_at: data.created_at ?? '',
          settings: parsedSettings,
          setup_required: data.setup_required ?? false,
          setup_completed_at: data.setup_completed_at ?? null,
          print_mode: data.print_mode ?? null,
        });

        const branding = resolveBranding({ id: data.id, name: data.name, slug: data.slug, settings: parsedSettings });
        writeBrandingCache(data.id, branding);
        // The tab always says PrintRun (the product), never "<org> ERP".
        document.title = 'PrintRun';
        // The tab icon is always the PrintRun favicon from index.html; an org's
        // own logo shows inside the app only.

        // Notify listeners that org change finished
        try {
          window.dispatchEvent(new CustomEvent('org-change-complete', { detail: { organizationId: data.id } }));
        } catch {
          /* ignore */
        }

        // Update Sentry context with organization
        try {
          Sentry.setContext('organization', {
            id: data.id,
            name: data.name,
            plan_type: data.plan_type,
            subscription_status: data.subscription_status,
          });
        } catch (sentryError) {
          // Non-critical - Sentry update failure doesn't affect functionality
          globalLogger.debug('Sentry organization context update failed', {
            operation: 'update_sentry_context',
            error: sentryError instanceof Error ? sentryError.message : String(sentryError),
          });
        }
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error');

        // Capture organization loading error in Sentry
        Sentry.captureException(error, {
          tags: {
            component: 'OrganizationContext',
            operation: 'loadOrganization',
            userId: user?.id,
          },
          extra: {
            organizationId: profile.organization_id,
            error: err.message,
          },
          level: 'error',
        });

        globalLogger.error('Failed to load organization', {
          operation: 'load_organization',
          error: err.message,
          userId: user?.id,
          organizationId: selectedOrgId,
        });

        toast({
          title: i18next.t('common:org.loadError'),
          description: i18next.t('common:org.loadErrorDescription'),
          variant: 'destructive',
        });

        // If the selected org cannot be loaded (e.g., membership removed), clear selection
        try {
          localStorage.removeItem('activeOrgId');
        } catch {
          void 0;
        }
        clearBrandingCache();
        setOrganization(null);
      } finally {
        setIsLoading(false);
        setInitialised(true);
      }
    };

    loadOrganization();
  }, [authLoading, user, profile, reloadTrigger, trpcUtils]);

  // React to org switcher changes
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { organizationId?: string } | undefined;
      const nextOrgId = detail?.organizationId || localStorage.getItem('activeOrgId') || null;
      // Only reload if the new org differs from what's currently loaded
      if (!nextOrgId || nextOrgId === organization?.id) return;
      // Clear org immediately so dependent queries (locations, etc.) disable
      // rather than erroring with the stale org ID
      setOrganization(null);
      setIsLoading(true);
      reloadOrganization();
    };
    window.addEventListener('org-changed', handler as EventListener);
    return () => window.removeEventListener('org-changed', handler as EventListener);
  }, [organization?.id]);

  const value: OrganizationContextType = {
    organization,
    isLoading,
    initialised,
    reloadOrganization,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};
